/**
 * demoInventoryProductMap.ts — Enlaza ventas demo de caja con productos reales del inventario.
 *
 * QUÉ HACE: Mapea cada punto de venta y IDs legados (sku-pc*, t1, may12-*) a cat_* del catálogo.
 * QUÉ EXPORTA: resolveDemoCatalogLine, getDemoProductLineForPunto, remapVentaProductosToCatalog.
 * QUÉ NO DEBE HACER: Mutar localStorage ni importar React.
 */

import { defaultProducts } from "@/mocks/data/products";
import type { Venta } from "../types/caja.types";

type CatalogProduct = (typeof defaultProducts)[number];

const CATALOG_BY_ID = new Map<string, CatalogProduct>(
  defaultProducts.map((p) => [String(p.id), p]),
);

/** 6 productos reales por punto de venta (índices 0–5 = líneas del turno). */
export const DEMO_PUNTO_CATALOG_IDS: Record<string, string[]> = {
  pc1: [
    "cat_acc_3",
    "cat_ropa_8",
    "cat_calz_3",
    "cat_acc_7",
    "cat_tech_4",
    "cat_acc_0",
  ],
  pc2: [
    "cat_beb_0",
    "cat_beb_2",
    "cat_lac_1",
    "cat_beb_9",
    "cat_beb_4",
    "cat_beb_6",
  ],
  pc3: [
    "cat_farm_0",
    "cat_farm_1",
    "cat_farm_2",
    "cat_farm_3",
    "cat_bell_4",
    "cat_farm_4",
  ],
  pc4: [
    "cat_rest_0",
    "cat_rest_1",
    "cat_rest_2",
    "cat_fast_0",
    "cat_desay_0",
    "cat_beb_7",
  ],
  pc5: [
    "cat_ropa_1",
    "cat_ropa_2",
    "cat_acc_0",
    "cat_acc_2",
    "cat_ropa_3",
    "cat_acc_5",
  ],
  pc6: [
    "cat_ferr_0",
    "cat_ferr_1",
    "cat_ferr_8",
    "cat_ferr_9",
    "cat_ferr_6",
    "cat_ferr_5",
  ],
  pc7: [
    "cat_pan_0",
    "cat_pan_2",
    "cat_pan_3",
    "cat_pan_4",
    "cat_pan_5",
    "cat_pan_6",
  ],
  pc8: [
    "cat_beb_9",
    "cat_beb_9",
    "cat_beb_2",
    "cat_beb_4",
    "cat_beb_5",
    "cat_snk_0",
  ],
  pc9: [
    "cat_tech_5",
    "cat_tech_4",
    "cat_elec_0",
    "cat_mini_4",
    "cat_acc_4",
    "cat_tech_6",
  ],
  pc10: [
    "cat_rest_3",
    "cat_fv_0",
    "cat_beb_2",
    "cat_snk_1",
    "cat_beb_0",
    "cat_pan_7",
  ],
};

/** IDs ficticios del seed may12 / multicaja → catálogo real. */
const LEGACY_PRODUCT_ID_TO_CATALOG: Record<string, string> = {
  t1: "cat_ropa_8",
  t2: "cat_acc_9",
  t3: "cat_ferr_1",
  t4: "cat_calz_0",
  t5: "cat_beb_7",
  t6: "cat_tech_3",
  t7: "cat_acc_7",
  n1: "cat_beb_2",
  n2: "cat_tech_4",
  n3: "cat_snk_0",
  n4: "cat_tech_2",
  n5: "cat_bell_4",
  n6: "cat_beb_0",
  "sku-m12-4a": "cat_ropa_8",
  "sku-m12-6": "cat_acc_3",
  "sku-m12-10": "cat_acc_7",
  "sku-m12-11": "cat_acc_3",
  "m12-p1": "cat_ropa_8",
  "m12-p2": "cat_acc_9",
  "m12-p3": "cat_ferr_1",
  "m12-p4": "cat_calz_0",
  "m12-p5": "cat_beb_7",
  "m12-p6": "cat_tech_3",
  "m12-p7": "cat_acc_7",
  "m12-p8": "cat_beb_2",
  "m12-p9": "cat_tech_4",
  "m12-p10": "cat_snk_0",
};

function catalogProduct(id: string): CatalogProduct | undefined {
  return CATALOG_BY_ID.get(id);
}

function findByNameHint(nombre: string): CatalogProduct | undefined {
  const hint = nombre
    .toLowerCase()
    .split("—")[0]
    .split(" - ")[0]
    .trim()
    .slice(0, 24);
  if (!hint) return undefined;
  return defaultProducts.find((p) => {
    const n = String(p.nombre).toLowerCase();
    return (
      n.includes(hint) || hint.includes(n.slice(0, Math.min(n.length, 18)))
    );
  });
}

function parseSkuPuntoIndex(
  productoId: string,
  _puntoCajaId?: string,
): { puntoId: string; index: number } | null {
  const match = productoId.match(/^sku-(pc\d+)-(\d+)$/);
  if (!match) return null;
  return { puntoId: match[1], index: parseInt(match[2], 10) - 1 };
}

export function getDemoProductLineForPunto(
  puntoId: string,
  lineIndex: number,
): { productoId: string; nombre: string } {
  const ids = DEMO_PUNTO_CATALOG_IDS[puntoId] ?? DEMO_PUNTO_CATALOG_IDS.pc1;
  const catalogId = ids[lineIndex % ids.length] ?? ids[0];
  const product = catalogProduct(catalogId);
  return {
    productoId: catalogId,
    nombre: product ? String(product.nombre) : `Producto demo ${lineIndex + 1}`,
  };
}

/** Resuelve un ID legado o cat_* a producto del catálogo inventario. */
export function resolveDemoCatalogLine(
  productoId: string,
  nombreFallback: string,
  puntoCajaId?: string,
): { productoId: string; nombre: string } {
  if (productoId.startsWith("cat_")) {
    const hit = catalogProduct(productoId);
    if (hit) {
      return { productoId, nombre: String(hit.nombre) };
    }
  }

  const legacy = LEGACY_PRODUCT_ID_TO_CATALOG[productoId];
  if (legacy) {
    const hit = catalogProduct(legacy);
    if (hit) {
      return { productoId: legacy, nombre: String(hit.nombre) };
    }
  }

  const skuParsed = parseSkuPuntoIndex(productoId, puntoCajaId);
  if (skuParsed) {
    return getDemoProductLineForPunto(skuParsed.puntoId, skuParsed.index);
  }

  if (productoId.startsWith("m12-p") || productoId.startsWith("sku-m12-")) {
    const byName = findByNameHint(nombreFallback);
    if (byName) {
      return { productoId: String(byName.id), nombre: String(byName.nombre) };
    }
  }

  const byName = findByNameHint(nombreFallback);
  if (byName) {
    return { productoId: String(byName.id), nombre: String(byName.nombre) };
  }

  return { productoId, nombre: nombreFallback };
}

export function remapVentaProductosToCatalog(venta: Venta): Venta {
  const productos = (venta.productos ?? []).map((line) => {
    const resolved = resolveDemoCatalogLine(
      line.productoId,
      line.nombre,
      venta.puntoCajaId,
    );
    return {
      ...line,
      productoId: resolved.productoId,
      nombre: resolved.nombre,
    };
  });
  return { ...venta, productos };
}
